import { type FC, Fragment, type ReactNode, useEffect, useState } from "react";
import { Col, Row } from "../../../../common/components/Layout";
import { MaterialInput } from "../../../../common/components/materialInput/MaterialInput";
import { ACCOUNT_NUMBER_LENGTH, ORDER_COMMENT_LENGTH, PO_NUMBER_LENGTH } from "../../../../utils/constants";
import Address, { type AddressData, type UnloadingPointData } from "../Address";
import OrderTotals from "../../CartPage/OrderTotals";
import Spacer from "../../../../common/components/Spacer";
import GridGappedContainer, {
  GridGappedContainerSize,
} from "../../../../common/components/GridGappedContainer/GridGappedContainer";
import classNames from "classnames";
import CheckoutSectionTitle from "../CheckoutSectionTitle";
import { type WeightData } from "../../../../models/WeightData";
import { type PriceData } from "../../../../models/PriceData";
import {
  type CreditCardData,
  type DeliveryModeData,
  type SupportedPaymentsTypesData,
} from "../../../transformers/CheckoutViewDataTransformer";
import {
  extractTopLevelLineItemsFromGrouping,
  selectCheckoutCart,
  selectDeliveryModeDetails,
  selectDeliveryModes,
  selectFreightPayment,
  selectFreightPaymentDetails,
  selectTotalCheckoutValue,
  selectTotalWeight,
} from "../../../redux/selectors/checkoutPageSelectors";
import { updateAddress, updateUnloadingPoint } from "../../../redux/actions/checkoutPageActions";
import { type CustomDispatch } from "../../../../redux/types";
import { connect, useSelector } from "react-redux";
import { type RootState } from "../../../../redux/store";
import { Trans, useTranslation } from "react-i18next";
import AddressDropdown from "../AddressDropdown/AddressDropdown";
import { Controller, type FieldErrors, useFormContext } from "react-hook-form";
import { CheckoutOptimeSubscriptionForm } from "./CheckoutOptimeSubscriptionForm";
import { CheckoutOptimeSubscriptionSummary } from "./CheckoutOptimeSubscriptionSummary";
import { FormMandatoryLabel } from "../FormMandatoryLabel";
import Parameter from "../../../../common/components/Parameter";
import {
  selectCartContainsDigitalServiceTenant,
  selectCartContainsOptime,
} from "../../../redux/selectors/productEntrySelectors";
import { Card } from "../../../../Configurators/Linear/components/Card";
import { useOptimeCountries } from "../CheckoutOptimeCountriesContext";
import { Permission, userHasPermission } from "../../../../utils/permissions";
import { URL_GENERAL_TERMS, URL_OPTIME_TERMS } from "../../../../utils/url/staticUrls";
import { SimpleSelect } from "../../../../common/components/Select/SimpleSelect";
import { type CheckoutForm } from "../CheckoutCart";
import { DeliveryOption, DeliveryOptionItem } from "../DeliveryOption";
import { CheckboxWithLabel } from "../../../../common/components/CheckboxWithLabel";
import { isKITProduct } from "../../../../models/ProductData";
import Radio from "../../../../common/components/Radio";
import { IconType } from "../../../../common/components/icon/IconType";
import { Icon } from "../../../../common/components/icon/Icon";
import AddCreditCard from "../CreditCard/addCreditCard";
import ManageCreditCard from "../CreditCard/manageCreditCard";
import { userIsConsumerDistributor, userIsDistributor } from "~/utils/roles";
import OrderTypeSelection from "~/Cart/components/CheckoutPage/OrderTypeSelection";
import { PAYMENT_TYPE_2_TRANSLATION_KEY } from "~/Cart/components/CheckoutPage/CheckoutSummary/constants";
import { getOrderType } from "~/utils/language/translationUtils";
import { type DropdownEntry } from "~/common/components/DropdownChoice/types";
import { TextField } from "@mui/material";
import { Alert } from "~/common/m3/Alert/Alert";

interface SelfProps {
  actionButtons: ReactNode[];
  cartProductsDeliveryDateList?: Date[];
  surcharge?: PriceData;
  tariffSurcharge?: boolean;
  bothButtonsDisabled: boolean;
}

interface FromStateProps {
  addresses: AddressData[];
  totalWeight: WeightData;
  totalValue: PriceData;
  soldTo: {
    name: string;
    accNo: string;
    addresses: AddressData[];
  };
  address?: AddressData;
  countriesList: {
    isocode: string;
    name: string;
  }[];
  canAddAddress: boolean;
  unloadingPoints?: Dict<UnloadingPointData[]>;
  unloadingPoint?: UnloadingPointData;
  defaultCreditCard: CreditCardData | undefined;
  supportedPaymentTypes: SupportedPaymentsTypesData[];
}

interface DispatchProps {
  updateAddress: (newAddress: AddressData, temporary?: boolean) => void;
  updateUnloadingPoint: (unloadingPoint: UnloadingPointData) => void;
}

type Props = SelfProps & FromStateProps & DispatchProps;

const Cmp: FC<Props> = (props) => {
  const {
    addresses,
    totalWeight,
    totalValue,
    address,
    soldTo,
    supportedPaymentTypes,
    defaultCreditCard,
    updateAddress,
    updateUnloadingPoint,
    actionButtons,
    countriesList,
    canAddAddress,
    unloadingPoints,
    unloadingPoint,
    surcharge,
    tariffSurcharge,
    bothButtonsDisabled,
  } = props;

  const {
    register,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<CheckoutForm>();

  const { t } = useTranslation();
  const [AddressesState, setAddressesState] = useState<AddressData[]>(addresses);

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenManage, setIsOpenManage] = useState(false);
  const [defaultCC, setDefaultCC] = useState(defaultCreditCard);

  const containsOptimeProducts = useSelector(selectCartContainsOptime);
  const containsDST = useSelector(selectCartContainsDigitalServiceTenant);

  const methodPayment = watch("paymentType");
  const deliveryCode = watch("deliveryCode");
  const freightPaymentID = watch("freightPaymentCode");
  const deliveryDetails = useSelector(selectDeliveryModeDetails(deliveryCode, methodPayment));
  const freightPaymentDetails = useSelector(selectFreightPaymentDetails(deliveryCode, methodPayment, freightPaymentID));

  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const hasKitProductsInQuotables = useSelector(selectHasKitProductsInQuotables);

  useEffect(() => {
    setAddressesState(addresses);
  }, [addresses]);

  const shippingMethodName = deliveryCode ? t(`data.shipmentMethod.${deliveryCode}`) : "-";

  const expressDelivery = "EXPRESS";
  let extraElements = undefined;
  if (deliveryDetails && deliveryDetails?.paymentCodeDisplay) {
    extraElements = [
      {
        label: t("checkout.shipmentInfo.label", {
          context: window.SCHA.config.translationCountryCode,
          shipmentType: shippingMethodName,
        }),
        value:
          deliveryDetails.paymentCodeDisplay === expressDelivery
            ? t(`data.paymentCodeDisplay.EXPRESS`, {
                context: window.SCHA.config.translationCountryCode,
              })
            : undefined,
      },
    ];
  }

  const availableUnloadingPoints =
    unloadingPoints && address && address.parentUnit && unloadingPoints[address.parentUnit]
      ? unloadingPoints[address.parentUnit]
      : [];

  const unloadingPointsElements = availableUnloadingPoints
    ? availableUnloadingPoints.map((element) => ({
        key: element.name,
        label: element.name,
        value: element.id,
      }))
    : [];

  const { cartProductsDeliveryDateList } = props;
  const countryByCode = useOptimeCountries().countryByCode;

  const canAddHeaderComments = userHasPermission(Permission.ADD_VIEW_HEADER_LEVEL_COMMENTS);

  const deliveryModes = useSelector(selectDeliveryModes(methodPayment));
  const FreightPaymentList = useSelector(selectFreightPayment(deliveryCode, methodPayment));

  const deliveryModeDropdownValues = (data?: DeliveryModeData[]): DropdownEntry<string>[] => {
    if (data) {
      return data.map((element) => ({
        key: element.code,
        label: t(`data.shipmentMethod.${element.code}`, {
          context: window.SCHA.config.translationCountryCode,
        }),
        value: element.code,
      }));
    } else {
      return [];
    }
  };

  // sort delivery dates, the farthers should be first
  cartProductsDeliveryDateList?.sort((a, b) => b.getTime() - a.getTime());

  const { ref: poNumberRef, ...poNumberProps } = register("poNumber", {
    required: true,
    maxLength: PO_NUMBER_LENGTH,
    validate: ($: string) => $.trim().length > 0,
  });

  const { ref: accountNumberRef, ...accountNumberProps } = register("freightPaymentAccountNumber", {
    required: false,
    maxLength: ACCOUNT_NUMBER_LENGTH,
    // TODO: analyse if we can do sth bette
    validate: ($: string) => !$ || $.trim().length > 0,
  });

  const isCardExpired = defaultCC && defaultCC.expired;

  const showAccountNumberInput = (errors: FieldErrors<CheckoutForm>) => {
    const selectedFreight = FreightPaymentList?.freightPayments
      ? FreightPaymentList?.freightPayments.find((item) => item.id === freightPaymentID)
      : undefined;
    if (selectedFreight && selectedFreight?.accountNumber === true) {
      return (
        <MaterialInput
          label={t("checkoutPage.headers.acccountNumber.input")}
          data-test-id={"checkout-input-accountNumber"}
          inputRef={accountNumberRef}
          error={!!errors.freightPaymentAccountNumber}
          {...accountNumberProps}
        />
      );
    }
    return;
  };

  const renderPaymenttText = () => {
    const renderText =
      methodPayment === "account" ? (
        <>{t("checkoutPage.payment.account")}</>
      ) : (
        <>
          {t("checkoutPage.creditCard.creditCardLabel")}
          <span className={"ml-1 mr-1 text-uppercase"}>{defaultCC?.type}</span>
          {t("checkoutPage.creditCard.creditCardLabelEnding")}
          <span className={"ml-1"}>{defaultCC?.number}</span>
        </>
      );

    return <CheckoutSummarySubItem title={renderText} />;
  };

  const renderFreightText = () => {
    const filtredPayment = FreightPaymentList?.freightPayments
      ? FreightPaymentList?.freightPayments.find((item) => item.id === freightPaymentID)
      : undefined;
    const renderText = filtredPayment !== undefined ? t(`data.freightPayment.${filtredPayment?.id}`) : "-";
    return <CheckoutSummarySubItem title={renderText} />;
  };

  const filtredFreight =
    deliveryModes &&
    deliveryModes?.deliveryModes &&
    deliveryModes?.deliveryModes.find((item) => item.code === deliveryCode);

  const isDistributor = userIsDistributor() || userIsConsumerDistributor();
  const availableOrderTypes = window.SCHA.config.availableOrderTypes;
  const selectedOrderType = window.SCHA.config.selectedOrderType;

  useEffect(() => {
    if (methodPayment !== "account" && defaultCC !== undefined) {
      setValue("creditCardID", defaultCC?.customerRefNum ? defaultCC.customerRefNum : "");
    } else {
      if (methodPayment === "card-jp" && defaultCC === undefined) {
        setValue("paymentType", "account");
      }
      setValue("creditCardID", "");
    }
  }, [methodPayment, defaultCC]);

  useEffect(() => {
    if (FreightPaymentList?.freightPayments !== undefined && FreightPaymentList?.freightPayments?.length > 0) {
      setValue(
        "freightPaymentCode",
        FreightPaymentList?.freightPayments[0]?.id ? FreightPaymentList?.freightPayments[0].id : "",
      );
    } else {
      setValue("freightPaymentCode", "");
    }
  }, [FreightPaymentList]);

  return (
    <>
      <Row className={"scha__order-summary"}>
        <Col size={3}>
          <Card className={"p-3"} disableHover={true}>
            <h2 className={"mt-0 title-medium mb-30"}>{t("checkoutPage.headers.orderInfo")}</h2>
            <div className="mb-16">
              <MaterialInput
                label={t("checkoutPage.headers.purchaseNumber.input")}
                data-test-id={"checkout-input-purchase-number"}
                error={!!errors.poNumber}
                inputRef={poNumberRef}
                {...poNumberProps}
              />
            </div>

            {isDistributor && availableOrderTypes && selectedOrderType && (
              <div className="mb-16">
                <Controller
                  name={"orderType"}
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <OrderTypeSelection availableOrderTypes={availableOrderTypes} value={value} onChange={onChange} />
                    );
                  }}
                />
              </div>
            )}

            {AddressesState.length === 1 && !canAddAddress ? (
              <Parameter label={t("checkoutPage.headers.shippingAddress")} className={"mb-30"}>
                {address && <Address data={address} />}
              </Parameter>
            ) : (
              <AddressDropdown
                addresses={AddressesState}
                selectedAddress={address!}
                addresssLabel={t("checkoutPage.addressDropdown.label")}
                updateAddress={updateAddress}
                customerNumber={soldTo.accNo}
                countriesList={countriesList}
                canAddAddress={canAddAddress}
              />
            )}
            <h4 className={"mt-0 title-medium mb-3"}>{t("checkoutPage.headers.payment")}</h4>
            <div className={"label-large mb-2 scha__order-summary-paymentType"}>
              {t("checkoutPage.payment.paymentType")}
            </div>
            <div className="mb-3">
              <Controller
                name={"paymentType"}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <>
                    {supportedPaymentTypes?.map((type, index) => (
                      <Fragment key={index}>
                        {type.code === "account" && (
                          <label className={"d-flex mb-2"}>
                            <Radio
                              active={value === type.code}
                              value={type.code}
                              onClick={onChange}
                              className={"icon-margin"}
                              data-test-id={""}
                            />
                            {t(PAYMENT_TYPE_2_TRANSLATION_KEY[type.code])}
                          </label>
                        )}
                        {type.code === "card-jp" && (
                          <>
                            {defaultCC && (
                              <>
                                <label
                                  className={classNames(
                                    "d-flex",
                                    isCardExpired && "scha__order-summary-option-disabled",
                                  )}
                                >
                                  <Radio
                                    active={value === type.code}
                                    value={type.code}
                                    onClick={onChange}
                                    className={"icon-margin"}
                                    data-test-id={""}
                                    disabled={isCardExpired}
                                  />
                                  {t(PAYMENT_TYPE_2_TRANSLATION_KEY[type.code])}
                                  <span className={"ml-1 mr-1 text-uppercase"}> {defaultCC.type}</span>
                                  {t("checkoutPage.creditCard.creditCardLabelEnding")}
                                  <span className={"ml-1 mr-1"}> {defaultCC.number}</span>
                                  <a
                                    className={"scha__order-summary-credit-card"}
                                    onClick={() => setIsOpenManage(!isOpenManage)}
                                  >
                                    {t("checkoutPage.creditCard.manageCardLabel")}
                                  </a>
                                </label>
                                {isCardExpired && (
                                  <p className={"scha__order-summary-credit-card-expired"}>
                                    {t("checkoutPage.creditCard.expiredCardLabel")}
                                  </p>
                                )}
                              </>
                            )}
                            {(!defaultCC || defaultCC === undefined) && (
                              <>
                                <div
                                  onClick={() => setIsOpenAdd(!isOpenAdd)}
                                  className={"d-flex scha__order-summary-add-credit-card"}
                                  key={String(index)}
                                >
                                  <Icon type={IconType.Plus} />
                                  {t("checkoutPage.creditCard.addCardLabel")}
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </Fragment>
                    ))}
                  </>
                )}
              />
            </div>

            <Controller
              name={"deliveryCode"}
              control={control}
              render={({ field: { value, onChange, onBlur } }) => {
                return (
                  <SimpleSelect
                    className={"mb-2"}
                    data-test-id={"checkout-dropdown-carrier"}
                    label={t("checkoutPage.headers.carrier")}
                    onChange={(e) => {
                      const deliveryCodeTarget = e.target.value as string;
                      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                      const newDetails = deliveryModes?.deliveryModes.find((item) => item.code === deliveryCodeTarget)!;
                      const orderType = newDetails.validOrderTypes.find(
                        (type) => type.code === window.SCHA.config.selectedOrderType,
                      );
                      if (!orderType) {
                        const newOrderType = newDetails.validOrderTypes[0].code;
                        setValue("orderType", newOrderType);
                        onChange(deliveryCodeTarget);
                      } else {
                        onChange(deliveryCodeTarget);
                      }
                    }}
                    onBlur={onBlur}
                    value={value}
                    values={
                      deliveryModes?.deliveryModes ? deliveryModeDropdownValues(deliveryModes?.deliveryModes) : []
                    }
                    fullWidth={true}
                  />
                );
              }}
              rules={{
                required: true,
              }}
            />

            {filtredFreight && filtredFreight.freightPayments && (
              <>
                <div className={"label-large mb-2 scha__order-summary-paymentType"}>
                  {t("checkoutPage.payment.freightPayment")}
                </div>
                <div className="mb-3">
                  <Controller
                    name={"freightPaymentCode"}
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <>
                          {filtredFreight.freightPayments.map((item, index) => (
                            <label className={"d-flex mb-2"} key={String(index)}>
                              <Radio
                                active={value === item.id}
                                value={item.id}
                                onClick={onChange}
                                className={"icon-margin"}
                                data-test-id={""}
                              />
                              {t(`data.freightPayment.${item.id}`)}
                            </label>
                          ))}
                        </>
                      );
                    }}
                  />
                </div>
              </>
            )}

            <div className="mb-2">{showAccountNumberInput(errors)}</div>

            {userHasPermission(Permission.COMPLETE_DELIVERY) && (
              <>
                <Parameter label={t("checkoutPage.headers.deliveryOptions.title")} className={"mb-2"}>
                  <Controller
                    name={"completeDelivery"}
                    control={control}
                    defaultValue={DeliveryOptionItem.PARTIAL}
                    rules={{ required: true }}
                    render={({ field: { value, onChange } }) => (
                      <DeliveryOption
                        onChange={onChange}
                        value={value}
                        estimatedDate={cartProductsDeliveryDateList && cartProductsDeliveryDateList[0]}
                      />
                    )}
                  />
                </Parameter>
                {watch("completeDelivery") === DeliveryOptionItem.COMPLETE && (
                  <Alert severity="info" className="mb-3">
                    {t("checkoutPage.headers.deliveryOptions.notice")}
                  </Alert>
                )}
              </>
            )}

            {unloadingPoint && unloadingPointsElements && unloadingPointsElements.length > 0 && (
              <SimpleSelect
                label={t("checkoutPage.headers.unloading")}
                className={"mt-30 mb-2"}
                values={unloadingPointsElements}
                data-test-id={"checkout-dropdown-uploading"}
                value={unloadingPoint!.id}
                onChange={(e) =>
                  updateUnloadingPoint(availableUnloadingPoints.find((item) => item.id == e.target.value)!)
                }
              />
            )}

            {canAddHeaderComments && (
              <div className={"mt-30 mb-30"}>
                <Controller
                  name={"orderComment"}
                  control={control}
                  rules={{ required: false, maxLength: ORDER_COMMENT_LENGTH }}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <TextField
                      variant={"outlined"}
                      inputProps={{ sx: { height: "108px !important" }, maxLength: ORDER_COMMENT_LENGTH }}
                      data-test-id={"checkout-header-comment"}
                      label={t("order.information.header.comment")}
                      helperText={t("order.information.header.helper")}
                      error={!!error}
                      inputRef={ref}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      fullWidth
                      {...inputProps}
                    />
                  )}
                />
              </div>
            )}

            {deliveryDetails && deliveryDetails?.shippingConditionCodes?.length > 0 && (
              <div className="scha__checkout__shipping-condition-messages">
                {deliveryDetails.shippingConditionCodes.map((code) => (
                  <Alert severity="info">
                    {t(`data.shippingCondition.${code}`, { context: window.SCHA.config.translationCountryCode })}
                  </Alert>
                ))}
              </div>
            )}
          </Card>
          <Card className={"mt-2 p-3"} disableHover={true}>
            {containsDST && <CheckoutOptimeSubscriptionForm control={control} />}
            <FormMandatoryLabel className={"mt-3"} />
          </Card>
        </Col>

        <Col size={9}>
          <CheckoutSectionTitle>{t("checkoutPage.headers.orderSummary")}</CheckoutSectionTitle>
          <Row>
            <Col size={4}>
              <CheckoutSummaryItem title={t("checkoutPage.headers.orderInfo")}>
                <CheckoutSummarySubItem title={t("checkoutPage.headers.purchaseNumber.review")}>
                  {watch("poNumber") || "-"}
                </CheckoutSummarySubItem>
                {isDistributor && availableOrderTypes && selectedOrderType && (
                  <CheckoutSummarySubItem title={t("header.selectedOrderType")}>
                    {getOrderType(t, watch("orderType"))}
                  </CheckoutSummarySubItem>
                )}
              </CheckoutSummaryItem>
              <CheckoutSummaryItem title={t("checkoutPage.headers.carrier")}>
                <CheckoutSummarySubItem title={shippingMethodName} />
              </CheckoutSummaryItem>
              <CheckoutSummaryItem
                title={t(getFreightHeaderLabel(deliveryDetails), {
                  context: window.SCHA.config.translationCountryCode,
                })}
              ></CheckoutSummaryItem>
              <CheckoutSummaryItem title={t("checkoutPage.headers.paymentType")}>
                <>{renderPaymenttText()}</>
              </CheckoutSummaryItem>
              {FreightPaymentList?.freightPayments !== undefined && (
                <CheckoutSummaryItem title={t("checkoutPage.headers.freightPayment")}>
                  <>{renderFreightText()}</>
                </CheckoutSummaryItem>
              )}
            </Col>

            <Col size={4}>
              <CheckoutSummaryItem title={t("checkoutPage.headers.shipTo")}>
                <CheckoutSummarySubItem title={t("checkoutPage.headers.shippingAddress")}>
                  {address && <Address data={address} />}
                </CheckoutSummarySubItem>
                {unloadingPoint && (
                  <CheckoutSummarySubItem title={t("checkoutPage.headers.unloading")}>
                    {unloadingPoint?.name}
                  </CheckoutSummarySubItem>
                )}
              </CheckoutSummaryItem>
            </Col>

            <Col size={4}>
              <CheckoutSummaryItem title={t("checkoutPage.headers.soldTo")}>
                <CheckoutSummarySubItem title={t("checkoutPage.headers.soldToAddress")}>
                  {soldTo.addresses &&
                    soldTo.addresses
                      .filter((address) => !address.editable)
                      .map((address) => <Address data={address} />)}
                </CheckoutSummarySubItem>
                <CheckoutSummarySubItem title={t("checkoutPage.headers.accountNumber")}>
                  {soldTo.accNo}
                </CheckoutSummarySubItem>
              </CheckoutSummaryItem>
            </Col>
          </Row>

          <Row className={"flex-justify-between"}>
            <Col size={4}>
              {deliveryDetails &&
              deliveryDetails?.paymentCodeDisplay &&
              deliveryDetails?.paymentCodeDisplay === expressDelivery
                ? t(`data.paymentCodeDisplay.EXPRESS`, {
                    context: window.SCHA.config.translationCountryCode,
                  })
                : "-"}
            </Col>
            {!containsDST && (
              <Col size={5} className={"d-flex flex-align-end"}>
                <OrderTotals
                  showPriceOnRequest={true}
                  className={"w-100 mb-3"}
                  weight={totalWeight}
                  totalValue={webservicesDisabled ? null : totalValue}
                  taxInNewLine={true}
                  rows={extraElements}
                  tariffSurcharge={tariffSurcharge}
                />
              </Col>
            )}
          </Row>

          {containsDST && (
            <CheckoutOptimeSubscriptionSummary
              email={watch("optime.email")}
              name={watch("optime.name")}
              lastName={watch("optime.lastName")}
              plant={watch("optime.plant")}
              timeZone={watch("optime.timeZone")}
              language={t(`languageName.${watch("optime.language")}`)}
              country={t(countryByCode[watch("optime.country") as string]) || ""}
            >
              <OrderTotals
                showPriceOnRequest={true}
                className={"w-100 mb-3"}
                weight={totalWeight}
                totalValue={totalValue}
                taxInNewLine={true}
                rows={extraElements}
                surcharge={surcharge}
                tariffSurcharge={tariffSurcharge}
              />
            </CheckoutOptimeSubscriptionSummary>
          )}

          <Spacer className={"mt-0 mb-30"} />

          {containsOptimeProducts && (
            <>
              <Alert severity={"info"} className={"mb-30"}>
                {t("page.checkout.optime.disclaimer")}
              </Alert>
              <p
                className={classNames(
                  "label-large mt-0 mb-2",
                  errors.optime?.consent ? "f-error-red" : "f-label-color",
                )}
              >
                {t("page.checkout.optime.terms.header")}
              </p>

              <Controller
                name={"optime.consent"}
                control={control}
                rules={{ required: true }}
                render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                  <CheckboxWithLabel
                    className={"mb-30"}
                    data-test-id={"checkout-optime-consent-cbox"}
                    inputRef={ref}
                    onCheck={onChange}
                    checked={value}
                    error={!!error}
                  >
                    <Trans i18nKey={"page.checkout.optime.terms.label"}>
                      Yes, I confirm that I have read, understood and accept the{" "}
                      <a href={URL_OPTIME_TERMS} target={"_blank"}>
                        General Terms and Conditions for Schaeffler Digital Services
                      </a>
                      .
                    </Trans>
                  </CheckboxWithLabel>
                )}
              />
            </>
          )}

          <Alert severity={"info"}>
            <Trans i18nKey={"page.checkout.legalDisclaimer"}>
              Our deliveries and services are subject to and governed by the{" "}
              <a href={URL_GENERAL_TERMS} target="_blank">
                Terms and Conditions (Sales)
              </a>{" "}
              of Schaeffler Group to the extent the respective deliveries and services are not governed by other written
              agreements in place between the parties. A binding contract is only concluded upon your receipt of our
              written order confirmation which confirms the terms upon which Schaeffler has accepted your order and
              concluded the contract of sale.
            </Trans>
          </Alert>

          {hasKitProductsInQuotables && (
            <Alert severity="info" className="mt-2">
              <div data-test-id={"info-housing-disclaimer-1"}>{t("page.checkout.housing.disclaimer.inQuotable")}</div>
            </Alert>
          )}

          {bothButtonsDisabled && (
            <Alert severity="info" className="mt-2">
              {t("feature.salesCampaign.checkout.allButtonsDisabledMsg")}
            </Alert>
          )}

          <GridGappedContainer gapSize={GridGappedContainerSize.S_32} className={"d-flex flex-justify-end mt-30"}>
            {actionButtons}
          </GridGappedContainer>
        </Col>
      </Row>
      {isOpenAdd && (
        <AddCreditCard
          updateCard={(data) => setDefaultCC(data)}
          closeModal={() => setIsOpenAdd(!isOpenAdd)}
          defaultCC={defaultCC ? true : false}
        />
      )}
      {isOpenManage && (
        <ManageCreditCard
          addNewCard={() => {
            setIsOpenManage(!isOpenManage);
            setIsOpenAdd(!isOpenAdd);
          }}
          updateCard={(data) => setDefaultCC(data)}
          closeModal={() => setIsOpenManage(!isOpenManage)}
          defaultCC={defaultCC}
        />
      )}
    </>
  );
};

export const CheckoutSummaryItem: FC<{ className?: string; title: ReactNode; children?: ReactNode }> = ({
  children,
  title,
  className,
}) => (
  <div className={classNames("my-30", className)}>
    <p className={"title-medium mt-0 mb-2"}>{title}</p>
    {children}
  </div>
);

export const CheckoutSummarySubItem: FC<{ className?: string; title: ReactNode; children?: ReactNode }> = ({
  children,
  title,
  className,
}) => (
  <div className={classNames("my-2", className)}>
    <p className={"f-label-color label-large mt-0 mb-1"}>{title}</p>
    {children}
  </div>
);

const mapStateToProps = (state: RootState): FromStateProps => ({
  address: state.checkoutPage.selectedAddress,
  soldTo: state.checkoutPage.soldTo,
  addresses: state.checkoutPage.addresses,
  totalValue: selectTotalCheckoutValue(state),
  totalWeight: selectTotalWeight(state),
  countriesList: state.checkoutPage.countriesList,
  canAddAddress: state.checkoutPage.canAddAddress,
  unloadingPoints: state.checkoutPage.unloadingPoints,
  unloadingPoint: state.checkoutPage.unloadingPoint,
  defaultCreditCard: state.checkoutPage.defaultCreditCard,
  supportedPaymentTypes: state.checkoutPage.supportedPaymentTypes,
});

const mapDispatchToProps = (dispatch: CustomDispatch<RootState>): DispatchProps => ({
  updateAddress: (newAddress, temporary?) => dispatch(updateAddress(newAddress, temporary)),
  updateUnloadingPoint: (unloadingPoint) => dispatch(updateUnloadingPoint(unloadingPoint)),
});

// TODO: Wait for https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31363 to be fixed
// @ts-ignore
const CheckoutSummary = connect(mapStateToProps, mapDispatchToProps)(Cmp) as FC<SelfProps>;
export default CheckoutSummary;

function getFreightHeaderLabel(deliveryDetails: DeliveryModeData | undefined | null) {
  return deliveryDetails?.paymentCodeInfo
    ? `data.shipment.freightCharges.${deliveryDetails.paymentCodeInfo}`
    : "checkoutPage.headers.freight";
}

const selectHasKitProductsInQuotables = (state: RootState) => {
  const quotables = selectCheckoutCart(state).quotables;

  return extractTopLevelLineItemsFromGrouping(quotables, state.cart.packEntriesById).some((lineItem) =>
    isKITProduct(state.products.basic[lineItem.productCode]),
  );
};
